import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBookOpen,
  FaChalkboardTeacher,
  FaExclamationTriangle,
  FaLayerGroup,
  FaMinusCircle,
  FaPen,
  FaPlusCircle,
  FaProjectDiagram,
  FaSchool,
  FaShapes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import AdminModalShell from "./AdminModalShell";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";


const emptyClassForm = {
  id: "",
  name: "",
  level: "",
  sections: "",
  subjects: [],
  classTeacher: "",
  studentCount: "",
};

const emptySubjectForm = {
  id: "",
  name: "",
  code: "",
  category: "Core",
  assignedTeacher: "",
  classCount: "",
};

const CLASS_LEVEL_OPTIONS = [
  { value: "PRE_PRIMARY", label: "Pre-Primary" },
  { value: "PRIMARY", label: "Primary" },
  { value: "MIDDLE", label: "Middle" },
  { value: "SECONDARY", label: "Secondary" },
  { value: "SENIOR_SECONDARY", label: "Senior Secondary" },
];

const normalizeClassLevel = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

  if (!normalized) return "";
  if (normalized === "MIDDLE_SCHOOL") return "MIDDLE";
  if (normalized === "SENIORSECONDARY") return "SENIOR_SECONDARY";
  if (CLASS_LEVEL_OPTIONS.some((item) => item.value === normalized)) return normalized;
  return normalized;
};

const normalizeClassRecord = (record = {}, index = 0) => ({
  id: record.id || record.classId || record._id || `class-${index + 1}`,
  name: record.name || record.className || record.standard || `Class ${index + 1}`,
  level: normalizeClassLevel(record.level || record.stage),
  sections: Array.isArray(record.sections)
    ? record.sections
    : String(record.sections || record.section || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
  subjects: Array.isArray(record.subjects)
    ? record.subjects
    : String(record.subjects || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
  subjectIds: Array.isArray(record.subjectIds)
    ? record.subjectIds
    : String(record.subjectIds || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
  classTeacher: record.classTeacher || record.teacherName || record.assignedTeacher || "Not assigned",
  studentCount: Number(record.studentCount ?? record.totalStudents ?? 0),
});

const normalizeSubjectRecord = (record = {}, index = 0) => ({
  id: record.id || record.subjectId || record._id || `subject-${index + 1}`,
  name: record.name || record.subjectName || `Subject ${index + 1}`,
  code: record.code || record.subjectCode || `SUB${index + 1}`,
  level: normalizeClassLevel(record.level || record.stage) || "-",
  category: record.category || "Core",
  assignedTeacher: record.assignedTeacher || record.teacherName || "Not assigned",
  classCount: Number(record.classCount ?? record.totalClasses ?? 0),
});

const extractList = (responseData, key) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const AdminClassSubjectManagement = ({ createMode = "" }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingClass, setSavingClass] = useState(false);
  const [savingSubject, setSavingSubject] = useState(false);
  const [dataSource, setDataSource] = useState("api");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [activeSection, setActiveSection] = useState("classes");
  const [editingClassId, setEditingClassId] = useState("");
  const [isAddingClassInline, setIsAddingClassInline] = useState(false);
  const [inlineClassForm, setInlineClassForm] = useState(emptyClassForm);
  const [subjectDropdownOpenFor, setSubjectDropdownOpenFor] = useState("");
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [classForm, setClassForm] = useState(emptyClassForm);
  const [subjectForm, setSubjectForm] = useState(emptySubjectForm);
  const [apiTeachers, setApiTeachers] = useState([]);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classResponse, subjectResponse, teacherResponse] = await Promise.allSettled([
          getCachedOrFetch(getTenantCacheKey("admin:classes"), () => api.get(API_PATHS.admin.classes()), { forceRefresh: refreshVersion > 0 }),
          getCachedOrFetch(getTenantCacheKey("admin:subjects"), () => api.get(API_PATHS.admin.subjects()), { forceRefresh: refreshVersion > 0 }),
          getCachedOrFetch(getTenantCacheKey("admin:teachers"), () => api.get(API_PATHS.admin.teachers()), { forceRefresh: refreshVersion > 0 }),
        ]);

        const nextClasses = classResponse.status === "fulfilled"
          ? extractList(classResponse.value.data, "classes").map(normalizeClassRecord)
          : [];
        const nextSubjects = subjectResponse.status === "fulfilled"
          ? extractList(subjectResponse.value.data, "subjects").map(normalizeSubjectRecord)
          : [];

        if (teacherResponse.status === "fulfilled") {
          const raw = teacherResponse.value?.data?.data ?? teacherResponse.value?.data ?? [];
          const list = Array.isArray(raw) ? raw : (raw?.teachers ?? raw?.content ?? []);
          setApiTeachers(
            list
              .map((t) => ({
                id: t.id || t._id || t.teacherId || "",
                name: t.name || t.teacherName || t.fullName || "",
              }))
              .filter((t) => t.name)
          );
        }

        setClasses(nextClasses);
        setSubjects(nextSubjects);
        setDataSource("api");
        if (createMode !== "class") {
          setSelectedClassId(nextClasses[0]?.id || "");
        } else {
          setSelectedClassId("");
        }
        if (createMode !== "subject") {
          setSelectedSubjectId(nextSubjects[0]?.id || "");
        } else {
          setSelectedSubjectId("");
        }
      } catch {
        setClasses([]);
        setSubjects([]);
        setDataSource("api");
        setSelectedClassId("");
        setSelectedSubjectId("");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshVersion, createMode]);

  useEffect(() => {
    if (createMode === "class") {
      setSelectedClassId("");
      setClassForm(emptyClassForm);
      setIsClassModalOpen(true);
    } else if (createMode === "subject") {
      setSelectedSubjectId("");
      setSubjectForm(emptySubjectForm);
      setIsSubjectModalOpen(true);
    }
  }, [createMode]);

  const filteredClasses = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return classes.filter((item) => (
      !query
      || item.name.toLowerCase().includes(query)
      || item.classTeacher.toLowerCase().includes(query)
      || item.sections.join(" ").toLowerCase().includes(query)
      || item.subjects.join(" ").toLowerCase().includes(query)
    ));
  }, [classes, searchTerm]);

  const filteredSubjects = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return subjects.filter((item) => (
      !query
      || item.name.toLowerCase().includes(query)
      || item.code.toLowerCase().includes(query)
      || item.assignedTeacher.toLowerCase().includes(query)
    ));
  }, [subjects, searchTerm]);

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) || null,
    [classes, selectedClassId]
  );

  const selectedSubject = useMemo(
    () => subjects.find((item) => item.id === selectedSubjectId) || null,
    [subjects, selectedSubjectId]
  );

  useEffect(() => {
    if (createMode === "class" || !selectedClassId || !selectedClass) {
      setClassForm(emptyClassForm);
      return;
    }

    const selectedSubjectIds = Array.isArray(selectedClass.subjectIds) && selectedClass.subjectIds.length > 0
      ? selectedClass.subjectIds
      : (selectedClass.subjects || [])
          .map((subjectName) => subjects.find((sub) => sub.name === subjectName)?.id)
          .filter(Boolean);

    setClassForm({
      id: selectedClass.id,
      name: selectedClass.name,
      level: selectedClass.level,
      sections: selectedClass.sections.join(", "),
      subjects: selectedSubjectIds,
      classTeacher: selectedClass.classTeacher,
      studentCount: String(selectedClass.studentCount || ""),
    });
  }, [selectedClass, selectedClassId, subjects, createMode]);

  useEffect(() => {
    if (createMode === "subject" || !selectedSubjectId || !selectedSubject) {
      setSubjectForm(emptySubjectForm);
      return;
    }

    setSubjectForm({
      id: selectedSubject.id,
      name: selectedSubject.name,
      code: selectedSubject.code,
      category: selectedSubject.category,
      assignedTeacher: selectedSubject.assignedTeacher,
      classCount: String(selectedSubject.classCount || ""),
    });
  }, [selectedSubject, selectedSubjectId, createMode]);

  const summary = useMemo(() => ({
    totalClasses: classes.length,
    totalSections: classes.reduce((sum, item) => sum + item.sections.length, 0),
    totalSubjects: subjects.length,
    mappedTeachers: new Set([...classes.map((item) => item.classTeacher), ...subjects.map((item) => item.assignedTeacher)].filter((value) => value && value !== "Not assigned")).size,
  }), [classes, subjects]);

  const panelClass = isDarkMode
    ? "border-slate-800 bg-slate-900 shadow-black/20"
    : "border-slate-200 bg-white shadow-slate-200/60";

  const tableWrapClass = isDarkMode
    ? "overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/70 shadow-lg shadow-black/20"
    : "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/70";

  const badgeBase = "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold";

  const updateClassForm = (key, value) => {
    setClassForm((current) => ({ ...current, [key]: value }));
  };

  const updateSubjectForm = (key, value) => {
    setSubjectForm((current) => ({ ...current, [key]: value }));
  };

  const resetClassForm = () => {
    setSelectedClassId("");
    setClassForm(emptyClassForm);
  };

  const resetSubjectForm = () => {
    setSelectedSubjectId("");
    setSubjectForm(emptySubjectForm);
  };

  const openCreateClassModal = () => {
    setSelectedClassId("");
    setClassForm(emptyClassForm);
    setIsClassModalOpen(true);
    if (createMode !== "class") {
      navigate("/admin/class-subjects/classes/create");
    }
  };

  const openEditClassModal = (classId) => {
    setSelectedClassId(classId);
    const targetClass = classes.find((item) => item.id === classId);
    if (targetClass) {
      const selectedSubjectIds = Array.isArray(targetClass.subjectIds) && targetClass.subjectIds.length > 0
        ? targetClass.subjectIds
        : (targetClass.subjects || [])
            .map((subjectName) => subjects.find((sub) => sub.name === subjectName)?.id)
            .filter(Boolean);
      setClassForm({
        id: targetClass.id,
        name: targetClass.name,
        level: targetClass.level,
        sections: targetClass.sections.join(", "),
        subjects: selectedSubjectIds,
        classTeacher: targetClass.classTeacher,
        studentCount: String(targetClass.studentCount || ""),
      });
    }
    setIsClassModalOpen(true);
  };

  const openCreateSubjectModal = () => {
    setSelectedSubjectId("");
    setSubjectForm(emptySubjectForm);
    setIsSubjectModalOpen(true);
    if (createMode !== "subject") {
      navigate("/admin/class-subjects/subjects/create");
    }
  };

  const openEditSubjectModal = (subjectId) => {
    setSelectedSubjectId(subjectId);
    const targetSubject = subjects.find((item) => item.id === subjectId);
    if (targetSubject) {
      setSubjectForm({
        id: targetSubject.id,
        name: targetSubject.name,
        code: targetSubject.code,
        category: targetSubject.category,
        assignedTeacher: targetSubject.assignedTeacher,
        classCount: String(targetSubject.classCount || ""),
      });
    }
    setIsSubjectModalOpen(true);
  };

  const closeClassModal = () => {
    setSelectedClassId("");
    setClassForm(emptyClassForm);
    setIsClassModalOpen(false);
    if (createMode === "class") {
      navigate("/admin/class-subjects");
    }
  };

  const closeSubjectModal = () => {
    setSelectedSubjectId("");
    setSubjectForm(emptySubjectForm);
    setIsSubjectModalOpen(false);
    if (createMode === "subject") {
      navigate("/admin/class-subjects");
    }
  };

  const upsertClass = (nextRecord) => {
    setClasses((current) => {
      const exists = current.some((item) => item.id === nextRecord.id);
      if (!exists) return [nextRecord, ...current];
      return current.map((item) => (item.id === nextRecord.id ? nextRecord : item));
    });
    setSelectedClassId(nextRecord.id);
  };

  const handleDeleteClass = async (classId) => {
    const confirmed = window.confirm("Delete this class?");
    if (!confirmed) return;

    try {
      await api.delete(API_PATHS.admin.classById(classId));
      setClasses((current) => current.filter((item) => item.id !== classId));
      if (selectedClassId === classId) {
        setSelectedClassId("");
      }
      if (editingClassId === classId) {
        cancelInlineClass();
      }
      invalidateCache(getTenantCacheKey("admin:classes"));
      toast.success("Class deleted.");
    } catch {
      toast.error("Unable to delete class. Please try again.");
    }
  };

  const startInlineAddClass = () => {
    setEditingClassId("");
    setInlineClassForm(emptyClassForm);
    setIsAddingClassInline(true);
    setSubjectDropdownOpenFor("");
  };

  const startInlineEditClass = (item) => {
    const selectedSubjectIds = Array.isArray(item.subjectIds) && item.subjectIds.length > 0
      ? item.subjectIds
      : (item.subjects || [])
          .map((subjectName) => subjects.find((sub) => sub.name === subjectName)?.id)
          .filter(Boolean);

    setIsAddingClassInline(false);
    setEditingClassId(item.id);
    setInlineClassForm({
      id: item.id,
      name: item.name,
      level: item.level,
      sections: item.sections.join(", "),
      subjects: selectedSubjectIds,
      classTeacher: item.classTeacher,
      studentCount: String(item.studentCount || ""),
    });
    setSubjectDropdownOpenFor("");
  };

  const cancelInlineClass = () => {
    setEditingClassId("");
    setIsAddingClassInline(false);
    setInlineClassForm(emptyClassForm);
    setSubjectDropdownOpenFor("");
  };

  const updateInlineClassForm = (key, value) => {
    setInlineClassForm((current) => ({ ...current, [key]: value }));
  };

  const toggleInlineSubjectDropdown = (dropdownKey) => {
    setSubjectDropdownOpenFor((current) => (current === dropdownKey ? "" : dropdownKey));
  };

  const renderInlineSubjectDropdown = (dropdownKey) => {
    const selectedIds = Array.isArray(inlineClassForm.subjects) ? inlineClassForm.subjects : [];
    const selectedNames = subjects
      .filter((sub) => selectedIds.includes(sub.id))
      .map((sub) => sub.name);
    const allSelected = subjects.length > 0 && subjects.every((sub) => selectedIds.includes(sub.id));

    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => toggleInlineSubjectDropdown(dropdownKey)}
          className="flex w-full items-center justify-between border px-2 py-1 text-xs"
        >
          <span className="truncate text-left">
            {selectedNames.length > 0 ? selectedNames.join(", ") : "Select subjects"}
          </span>
          <span>{subjectDropdownOpenFor === dropdownKey ? "^" : "v"}</span>
        </button>

        {subjectDropdownOpenFor === dropdownKey && (
          <div className={`absolute z-20 mt-1 max-h-44 w-64 overflow-y-auto border p-2 shadow-lg ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-300 bg-white"}`}>
            <label className="flex items-center gap-2 border-b pb-2 text-xs font-semibold">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => {
                  updateInlineClassForm("subjects", allSelected ? [] : subjects.map((sub) => sub.id));
                  setSubjectDropdownOpenFor("");
                }}
              />
              <span>Select all</span>
            </label>

            <div className="mt-2 space-y-1">
              {subjects.map((sub) => {
                const checked = selectedIds.includes(sub.id);
                return (
                  <label key={sub.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? selectedIds.filter((id) => id !== sub.id)
                          : [...selectedIds, sub.id];
                        updateInlineClassForm("subjects", next);
                        setSubjectDropdownOpenFor("");
                      }}
                    />
                    <span>{sub.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const saveInlineClass = async () => {
    const selectedSubjects = subjects.filter((sub) => (inlineClassForm.subjects || []).includes(sub.id));
    const payload = {
      name: String(inlineClassForm.name || "").trim(),
      level: String(inlineClassForm.level || "").trim(),
      sections: String(inlineClassForm.sections || "").split(",").map((item) => item.trim()).filter(Boolean),
      subjectIds: Array.isArray(inlineClassForm.subjects) ? inlineClassForm.subjects : [],
      subjects: selectedSubjects.map((sub) => sub.name),
      classTeacher: String(inlineClassForm.classTeacher || "").trim(),
      studentCount: Number(inlineClassForm.studentCount || 0),
    };

    if (!payload.name || !payload.level || payload.sections.length === 0 || !payload.classTeacher) {
      toast.error("Please fill required class fields.");
      return;
    }

    setSavingClass(true);
    try {
      const isEditing = Boolean(editingClassId);
      const response = isEditing
        ? await api.put(API_PATHS.admin.classById(editingClassId), payload)
        : await api.post(API_PATHS.admin.classes(), payload);

      const nextRecord = normalizeClassRecord(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? editingClassId : undefined },
        classes.length
      );

      upsertClass(nextRecord);
      invalidateCache(getTenantCacheKey("admin:classes"));
      cancelInlineClass();
      toast.success(isEditing ? "Class updated." : "Class created.");
    } catch {
      toast.error("Unable to save class. Please try again.");
    } finally {
      setSavingClass(false);
    }
  };

  const upsertSubject = (nextRecord) => {
    setSubjects((current) => {
      const exists = current.some((item) => item.id === nextRecord.id);
      if (!exists) return [nextRecord, ...current];
      return current.map((item) => (item.id === nextRecord.id ? nextRecord : item));
    });
    setSelectedSubjectId(nextRecord.id);
  };

  const handleDeleteSubject = async (subjectId) => {
    const confirmed = window.confirm("Delete this subject?");
    if (!confirmed) return;

    try {
      await api.delete(API_PATHS.admin.subjectById(subjectId));
      setSubjects((current) => current.filter((item) => item.id !== subjectId));
      setClasses((current) => current.map((item) => ({
        ...item,
        subjectIds: (item.subjectIds || []).filter((id) => id !== subjectId),
        subjects: (item.subjects || []).filter((name) => name !== (subjects.find((sub) => sub.id === subjectId)?.name || "")),
      })));
      if (selectedSubjectId === subjectId) {
        setSelectedSubjectId("");
      }
      invalidateCache(getTenantCacheKey("admin:subjects"));
      toast.success("Subject deleted.");
    } catch {
      toast.error("Unable to delete subject. Please try again.");
    }
  };

  const handleSaveClass = async (event) => {
    event.preventDefault();
    setSavingClass(true);

    const selectedSubjects = subjects.filter((sub) => classForm.subjects.includes(sub.id));

    const payload = {
      name: classForm.name.trim(),
      level: classForm.level.trim(),
      sections: classForm.sections.split(",").map((item) => item.trim()).filter(Boolean),
      subjectIds: Array.isArray(classForm.subjects) ? classForm.subjects : [],
      subjects: selectedSubjects.map((sub) => sub.name),
      classTeacher: classForm.classTeacher.trim(),
      studentCount: Number(classForm.studentCount || 0),
    };

    const isEditing = Boolean(classForm.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.classById(classForm.id), payload)
        : await api.post(API_PATHS.admin.classes(), payload);

      const nextRecord = normalizeClassRecord(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? classForm.id : undefined },
        classes.length
      );
      upsertClass(nextRecord);
      invalidateCache(getTenantCacheKey("admin:classes"));
      setSelectedClassId("");
      setClassForm(emptyClassForm);
      setIsClassModalOpen(false);
      if (createMode === "class") navigate("/admin/class-subjects");
      setDataSource("api");
      toast.success(isEditing ? "Class updated." : "Class created.");
    } catch {
      toast.error("Unable to save class. Please try again.");
    } finally {
      setSavingClass(false);
    }
  };

  const handleSaveSubject = async (event) => {
    event.preventDefault();
    setSavingSubject(true);

    const payload = {
      name: subjectForm.name.trim(),
      code: subjectForm.code.trim(),
      category: subjectForm.category,
      assignedTeacher: subjectForm.assignedTeacher.trim(),
      classCount: Number(subjectForm.classCount || 0),
    };

    const isEditing = Boolean(subjectForm.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.subjectById(subjectForm.id), payload)
        : await api.post(API_PATHS.admin.subjects(), payload);

      const nextRecord = normalizeSubjectRecord(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? subjectForm.id : undefined },
        subjects.length
      );
      upsertSubject(nextRecord);
      invalidateCache(getTenantCacheKey("admin:subjects"));
      setSelectedSubjectId("");
      setSubjectForm(emptySubjectForm);
      setIsSubjectModalOpen(false);
      if (createMode === "subject") navigate("/admin/class-subjects");
      setDataSource("api");
      toast.success(isEditing ? "Subject updated." : "Subject created.");
    } catch {
      toast.error("Unable to save subject. Please try again.");
    } finally {
      setSavingSubject(false);
    }
  };

  return (
    <div>
      <div className="space-y-0">
        <section>
          {/* Activity menu tabs */}
          <AdminResponsiveTabs tabs={[{ key: "metrics", label: "Metrics" }, { key: "classes", label: "Classes" }, { key: "subjects", label: "Subjects" }]} activeKey={activeSection} onChange={setActiveSection} tone="sky" isDarkMode={isDarkMode} />

          {/* Metrics panel */}
          {activeSection === "metrics" && (
            <div className={`border-x border-b px-6 py-8 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: "Total Classes", value: summary.totalClasses },
                  { label: "Total Sections", value: summary.totalSections },
                  { label: "Total Subjects", value: summary.totalSubjects },
                  { label: "Mapped Teachers", value: summary.mappedTeachers },
                ].map((metric) => (
                  <div key={metric.label} className={`rounded border p-4 ${isDarkMode ? "border-slate-700 bg-slate-800" : "border-gray-100 bg-gray-50"}`}>
                    <p className={`text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>{metric.label}</p>
                    <p className={`mt-2 text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>{metric.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subject directory */}
          {activeSection === "subjects" && (
            <div className={`border-x border-b shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
              <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4 ${isDarkMode ? "border-slate-800" : "border-gray-100"}`}>
                <div className="flex items-center gap-3">
                  <h2 className={`text-base font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>Subjects</h2>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${isDarkMode ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500"}`}>{filteredSubjects.length} records</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-300" : "border-gray-200 bg-gray-50 text-gray-600"}`}>
                    <svg className="h-3.5 w-3.5 opacity-50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-48 bg-transparent text-sm focus:outline-none placeholder:opacity-50" placeholder="Search subjects…" />
                  </div>
                  <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                  <ModuleImportButton moduleKey="subjects" label="Import subjects" />
                  <button type="button" onClick={openCreateSubjectModal} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${isDarkMode ? "bg-indigo-600 text-white hover:bg-indigo-500" : "bg-indigo-500 text-white hover:bg-indigo-600"}`}>
                    <FaPlusCircle className="text-[10px]" /> Add subject
                  </button>
                </div>
              </div>
              {loading ? (
                <div className={`px-6 py-16 text-center text-sm ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>Loading subject records…</div>
              ) : filteredSubjects.length === 0 ? (
                <div className={`px-6 py-16 text-center text-sm ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>No subject records available.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? "border-slate-800 bg-slate-900/60" : "border-gray-100 bg-gray-50/80"}`}>
                        <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Subject</th>
                        <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Code</th>
                        <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Category</th>
                        <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Teacher</th>
                        <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Classes</th>
                        <th className={`px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubjects.map((item) => (
                        <tr key={item.id} className={`border-b transition-colors ${isDarkMode ? "border-slate-800/60 hover:bg-slate-800/30" : "border-gray-100 hover:bg-gray-50/70"}`}>
                          <td className={`px-6 py-4 font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>{item.name}</td>
                          <td className={`px-6 py-4 font-mono text-sm ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>{item.code}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium ${isDarkMode ? "bg-indigo-900/40 text-indigo-300" : "bg-indigo-50 text-indigo-700"}`}>{item.category}</span>
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDarkMode ? "text-slate-300" : "text-gray-600"}`}>{item.assignedTeacher}</td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDarkMode ? "text-slate-200" : "text-gray-700"}`}>{item.classCount}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => openEditSubjectModal(item.id)} className={`rounded border px-3 py-1.5 text-xs font-semibold transition ${isDarkMode ? "border-slate-600 text-slate-300 hover:border-indigo-500 hover:text-indigo-400" : "border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600"}`}>Edit</button>
                              <button type="button" onClick={() => handleDeleteSubject(item.id)} title="Delete" className={`rounded border px-2 py-1.5 text-xs font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-500 hover:border-red-500 hover:text-red-400" : "border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-500"}`}><FaMinusCircle /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Class directory */}
          {activeSection === "classes" && <div className={`border-x border-b shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
            {/* Toolbar */}
            <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4 ${isDarkMode ? "border-slate-800" : "border-gray-100"}`}>
              <div className="flex items-center gap-3">
                <h2 className={`text-base font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>Classes</h2>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${isDarkMode ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500"}`}>
                  {filteredClasses.length} records
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-300" : "border-gray-200 bg-gray-50 text-gray-600"}`}>
                  <svg className="h-3.5 w-3.5 opacity-50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-48 bg-transparent text-sm focus:outline-none placeholder:opacity-50"
                    placeholder="Search classes…"
                  />
                </div>
                  <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                  <ModuleImportButton moduleKey="classes" label="Import classes" />
                  <button
                  type="button"
                  onClick={openCreateClassModal}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${isDarkMode ? "bg-sky-600 text-white hover:bg-sky-500" : "bg-sky-500 text-white hover:bg-sky-600"}`}
                >
                  <FaPlusCircle className="text-[10px]" /> Add class
                </button>
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className={`px-6 py-16 text-center text-sm ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>Loading class records…</div>
            ) : filteredClasses.length === 0 ? (
              <div className={`px-6 py-16 text-center text-sm ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>No class records available.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? "border-slate-800 bg-slate-900/60" : "border-gray-100 bg-gray-50/80"}`}>
                      <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Class</th>
                      <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Level</th>
                      <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Teacher</th>
                      <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Sections</th>
                      <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Subjects</th>
                      <th className={`px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Students</th>
                      <th className={`px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isAddingClassInline && (
                      <tr className={`border-b ${isDarkMode ? "border-slate-800 bg-slate-800/40" : "border-gray-100 bg-sky-50/50"}`}>
                        <td className="px-6 py-3">
                          <input type="text" value={inlineClassForm.name} onChange={(event) => updateInlineClassForm("name", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} placeholder="Class 10" />
                        </td>
                        <td className="px-6 py-3">
                          <select value={inlineClassForm.level} onChange={(event) => updateInlineClassForm("level", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`}>
                            <option value="">Level</option>
                            {CLASS_LEVEL_OPTIONS.map((level) => (<option key={level.value} value={level.value}>{level.label}</option>))}
                          </select>
                        </td>
                        <td className="px-6 py-3">
                          {apiTeachers.length > 0 ? (
                            <select value={inlineClassForm.classTeacher} onChange={(event) => updateInlineClassForm("classTeacher", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`}>
                              <option value="">Select teacher</option>
                              {apiTeachers.map((t) => (<option key={t.id || t.name} value={t.name}>{t.name}</option>))}
                            </select>
                          ) : (
                            <input type="text" value={inlineClassForm.classTeacher} onChange={(event) => updateInlineClassForm("classTeacher", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} placeholder="Teacher" />
                          )}
                        </td>
                        <td className="px-6 py-3">
                          <input type="text" value={inlineClassForm.sections} onChange={(event) => updateInlineClassForm("sections", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} placeholder="A, B" />
                        </td>
                        <td className="px-6 py-3">{renderInlineSubjectDropdown("add")}</td>
                        <td className="px-6 py-3">
                          <input type="number" min="0" value={inlineClassForm.studentCount} onChange={(event) => updateInlineClassForm("studentCount", event.target.value)} className={`w-20 rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} placeholder="0" />
                        </td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={saveInlineClass} disabled={savingClass} className="rounded border border-sky-500 px-3 py-1.5 text-xs font-semibold text-sky-500 transition hover:bg-sky-500 hover:text-white">{savingClass ? "Saving…" : "Save"}</button>
                            <button type="button" onClick={cancelInlineClass} className={`rounded border px-3 py-1.5 text-xs font-semibold transition ${isDarkMode ? "border-slate-600 text-slate-400 hover:bg-slate-800" : "border-gray-300 text-gray-500 hover:bg-gray-100"}`}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {filteredClasses.map((item) => {
                      const isEditingRow = editingClassId === item.id;
                      const levelColors = {
                        PRIMARY: isDarkMode ? "bg-emerald-900/40 text-emerald-300" : "bg-emerald-50 text-emerald-700",
                        MIDDLE: isDarkMode ? "bg-sky-900/40 text-sky-300" : "bg-sky-50 text-sky-700",
                        SECONDARY: isDarkMode ? "bg-violet-900/40 text-violet-300" : "bg-violet-50 text-violet-700",
                        SENIOR_SECONDARY: isDarkMode ? "bg-orange-900/40 text-orange-300" : "bg-orange-50 text-orange-700",
                        PRE_PRIMARY: isDarkMode ? "bg-pink-900/40 text-pink-300" : "bg-pink-50 text-pink-700",
                      };
                      const levelColor = levelColors[item.level] || (isDarkMode ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500");
                      return (
                        <tr key={item.id} className={`border-b transition-colors ${isDarkMode ? "border-slate-800/60 hover:bg-slate-800/30" : "border-gray-100 hover:bg-gray-50/70"}`}>
                          {isEditingRow ? (
                            <>
                              <td className="px-6 py-3">
                                <input type="text" value={inlineClassForm.name} onChange={(event) => updateInlineClassForm("name", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} />
                              </td>
                              <td className="px-6 py-3">
                                <select value={inlineClassForm.level} onChange={(event) => updateInlineClassForm("level", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`}>
                                  <option value="">Level</option>
                                  {CLASS_LEVEL_OPTIONS.map((level) => (<option key={level.value} value={level.value}>{level.label}</option>))}
                                </select>
                              </td>
                              <td className="px-6 py-3">
                                {apiTeachers.length > 0 ? (
                                  <select value={inlineClassForm.classTeacher} onChange={(event) => updateInlineClassForm("classTeacher", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`}>
                                    <option value="">Select teacher</option>
                                    {apiTeachers.map((t) => (<option key={t.id || t.name} value={t.name}>{t.name}</option>))}
                                  </select>
                                ) : (
                                  <input type="text" value={inlineClassForm.classTeacher} onChange={(event) => updateInlineClassForm("classTeacher", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} />
                                )}
                              </td>
                              <td className="px-6 py-3"><input type="text" value={inlineClassForm.sections} onChange={(event) => updateInlineClassForm("sections", event.target.value)} className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} /></td>
                              <td className="px-6 py-3">{renderInlineSubjectDropdown(`edit-${item.id}`)}</td>
                              <td className="px-6 py-3"><input type="number" min="0" value={inlineClassForm.studentCount} onChange={(event) => updateInlineClassForm("studentCount", event.target.value)} className={`w-20 rounded border px-2 py-1.5 text-sm focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-gray-300 bg-white"}`} /></td>
                              <td className="px-6 py-3 text-right">
                                <div className="flex justify-end gap-2">
                                  <button type="button" onClick={saveInlineClass} disabled={savingClass} className="rounded border border-sky-500 px-3 py-1.5 text-xs font-semibold text-sky-500 transition hover:bg-sky-500 hover:text-white">{savingClass ? "Saving…" : "Save"}</button>
                                  <button type="button" onClick={cancelInlineClass} className={`rounded border px-3 py-1.5 text-xs font-semibold transition ${isDarkMode ? "border-slate-600 text-slate-400 hover:bg-slate-800" : "border-gray-300 text-gray-500 hover:bg-gray-100"}`}>Cancel</button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className={`flex h-8 w-8 items-center justify-center rounded text-xs font-bold ${isDarkMode ? "bg-sky-900/50 text-sky-300" : "bg-sky-100 text-sky-600"}`}>
                                    {item.name.charAt(0).toUpperCase()}
                                  </div>
                                  <p className={`font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>{item.name}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium ${levelColor}`}>{item.level || "—"}</span>
                              </td>
                              <td className={`px-6 py-4 text-sm ${isDarkMode ? "text-slate-300" : "text-gray-600"}`}>{item.classTeacher}</td>
                              <td className="px-6 py-4">
                                <div className="flex flex-wrap gap-1">
                                  {(item.sections.length ? item.sections : ["-"]).map((s, i) => (
                                    <span key={i} className={`rounded px-2 py-0.5 text-[11px] font-medium ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-gray-100 text-gray-600"}`}>{s}</span>
                                  ))}
                                </div>
                              </td>
                              <td className={`px-6 py-4 max-w-[220px] truncate text-sm ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>{(item.subjects || []).join(", ") || "—"}</td>
                              <td className={`px-6 py-4 text-sm font-medium ${isDarkMode ? "text-slate-200" : "text-gray-700"}`}>{item.studentCount}</td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button type="button" onClick={() => startInlineEditClass(item)} className={`rounded border px-3 py-1.5 text-xs font-semibold transition ${isDarkMode ? "border-slate-600 text-slate-300 hover:border-sky-500 hover:text-sky-400" : "border-gray-300 text-gray-600 hover:border-sky-400 hover:text-sky-600"}`}>
                                    Edit
                                  </button>
                                  <button type="button" onClick={() => handleDeleteClass(item.id)} title="Delete" className={`rounded border px-2 py-1.5 text-xs font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-500 hover:border-red-500 hover:text-red-400" : "border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-500"}`}>
                                    <FaMinusCircle />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>}
        </section>

        <AdminModalShell isOpen={isClassModalOpen || createMode === "class"} onClose={closeClassModal} isDarkMode={isDarkMode} maxWidth="max-w-3xl" pageMode={createMode === "class"}>
          <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
            <div className="flex items-center justify-between gap-4 pr-12">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Class form</p>
                <h2 className="mt-2 text-2xl font-bold">{classForm.id ? "Update class" : "Create class"}</h2>
              </div>
              <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>Popup form</div>
            </div>

            <form onSubmit={handleSaveClass} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Class name</label>
                  <input type="text" value={classForm.name} onChange={(event) => updateClassForm("name", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="Class 10" required />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Level</label>
                  <select
                    value={classForm.level}
                    onChange={(event) => updateClassForm("level", event.target.value)}
                    className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                    required
                  >
                    <option value="">Select level</option>
                    {CLASS_LEVEL_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Sections</label>
                  <input type="text" value={classForm.sections} onChange={(event) => updateClassForm("sections", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="A, B, C" required />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Subjects</label>
                  <div className={`max-h-44 overflow-y-auto rounded-2xl border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-950" : "border-slate-300 bg-white"}`}>
                    {subjects.length === 0 ? (
                      <p className={`py-2 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No subjects available</p>
                    ) : (
                      subjects.map((sub) => {
                        const isChecked = Array.isArray(classForm.subjects) && classForm.subjects.includes(sub.id);
                        return (
                          <label key={sub.id} className={`flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-sm ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-sky-50"}`}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                const current = Array.isArray(classForm.subjects) ? classForm.subjects : [];
                                const next = isChecked
                                  ? current.filter((id) => id !== sub.id)
                                  : [...current, sub.id];
                                updateClassForm("subjects", next);
                              }}
                              className="accent-sky-500"
                            />
                            <span>{sub.name}</span>
                            {sub.code && <span className="text-xs text-slate-400">({sub.code})</span>}
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>
                    Selected: {Array.isArray(classForm.subjects) ? classForm.subjects.length : 0}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Class teacher</label>
                  {apiTeachers.length > 0 ? (
                    <select
                      value={classForm.classTeacher}
                      onChange={(event) => updateClassForm("classTeacher", event.target.value)}
                      className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                      required
                    >
                      <option value="">— Select teacher —</option>
                      {apiTeachers.map((t) => (
                        <option key={t.id || t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={classForm.classTeacher} onChange={(event) => updateClassForm("classTeacher", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="Rahul Patil" required />
                  )}
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Student count</label>
                  <input type="number" min="0" value={classForm.studentCount} onChange={(event) => updateClassForm("studentCount", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="72" required />
                </div>
              </div>
              <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                Sections and subjects are saved as structured values so the next timetable module can assign periods without rebuilding the academic base.
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={savingClass} className="inline-flex items-center rounded-full bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-70">{savingClass ? "Saving..." : classForm.id ? "Update class" : "Create class"}</button>
                <button type="button" onClick={resetClassForm} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Reset</button>
              </div>
            </form>
          </div>
        </AdminModalShell>

        <AdminModalShell isOpen={isSubjectModalOpen || createMode === "subject"} onClose={closeSubjectModal} isDarkMode={isDarkMode} maxWidth="max-w-3xl" pageMode={createMode === "subject"}>
          <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
            <div className="flex items-center justify-between gap-4 pr-12">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Subject form</p>
                <h2 className="mt-2 text-2xl font-bold">{subjectForm.id ? "Update subject" : "Create subject"}</h2>
              </div>
              <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>Popup form</div>
            </div>

            <form onSubmit={handleSaveSubject} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Subject name</label>
                  <input type="text" value={subjectForm.name} onChange={(event) => updateSubjectForm("name", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" placeholder="Mathematics" required />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Code</label>
                  <input type="text" value={subjectForm.code} onChange={(event) => updateSubjectForm("code", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" placeholder="MATH" required />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Category</label>
                  <select value={subjectForm.category} onChange={(event) => updateSubjectForm("category", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100">
                    <option value="Core">Core</option>
                    <option value="Language">Language</option>
                    <option value="Elective">Elective</option>
                  </select>
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Assigned teacher</label>
                  {apiTeachers.length > 0 ? (
                    <select
                      value={subjectForm.assignedTeacher}
                      onChange={(event) => updateSubjectForm("assignedTeacher", event.target.value)}
                      className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                      required
                    >
                      <option value="">— Select teacher —</option>
                      {apiTeachers.map((t) => (
                        <option key={t.id || t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={subjectForm.assignedTeacher} onChange={(event) => updateSubjectForm("assignedTeacher", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" placeholder="Rahul Patil" required />
                  )}
                </div>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Mapped class count</label>
                <input type="number" min="0" value={subjectForm.classCount} onChange={(event) => updateSubjectForm("classCount", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100" placeholder="2" required />
              </div>
              <div className={`grid gap-3 rounded-2xl border p-4 sm:grid-cols-3 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-start gap-3"><div className={`mt-1 flex h-10 w-10 items-center justify-center rounded-2xl ${isDarkMode ? "bg-slate-800 text-sky-200" : "bg-white text-sky-700"}`}><FaShapes /></div><div><p className="text-sm font-semibold">Sections</p><p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Keep each class split clear before scheduling.</p></div></div>
                <div className="flex items-start gap-3"><div className={`mt-1 flex h-10 w-10 items-center justify-center rounded-2xl ${isDarkMode ? "bg-slate-800 text-sky-200" : "bg-white text-sky-700"}`}><FaBookOpen /></div><div><p className="text-sm font-semibold">Subjects</p><p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Each subject keeps its own code and category.</p></div></div>
                <div className="flex items-start gap-3"><div className={`mt-1 flex h-10 w-10 items-center justify-center rounded-2xl ${isDarkMode ? "bg-slate-800 text-sky-200" : "bg-white text-sky-700"}`}><FaProjectDiagram /></div><div><p className="text-sm font-semibold">Mapping</p><p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Teacher mapping prepares the next timetable step.</p></div></div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={savingSubject} className="inline-flex items-center rounded-full bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-600 disabled:opacity-70">{savingSubject ? "Saving..." : subjectForm.id ? "Update subject" : "Create subject"}</button>
                <button type="button" onClick={resetSubjectForm} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Reset</button>
              </div>
            </form>
          </div>
        </AdminModalShell>
      </div>
    </div>
  );
};

export default AdminClassSubjectManagement;
